import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import {
  Modal,
  Timeline,
  Tag,
  Descriptions,
  Empty,
  Alert,
  Spin,
  Button
} from 'antd'
import { ReloadOutlined } from '@ant-design/icons'
import { getRequest, dutyTransactionTimelineUrl } from '../config'

// Normalized status (from the API) → colour. Gateway-agnostic, so a future gateway maps in
// without changing this.
const STATUS_COLOR = {
  success: 'green',
  failed: 'red',
  abandoned: 'volcano',
  ongoing: 'gold',
  pending: 'gold',
  reversed: 'purple',
  initialized: 'blue',
  no_attempt: 'default',
  unknown: 'default'
}

const EVENT_COLOR = {
  error: 'red',
  success: 'green',
  auth: 'blue',
  input: 'blue',
  action: 'blue',
  open: 'gray',
  close: 'gray',
  info: 'gray'
}

const PaymentTimelineModal = ({ transaction, open, onClose }) => {
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState(null)

  const load = async () => {
    if (!transaction || !transaction.id) return
    const res = await getRequest(
      dutyTransactionTimelineUrl(transaction.id),
      'payment timeline',
      setLoading
    )
    if (res.success) setData(res.data)
  }

  useEffect(() => {
    if (open && transaction) {
      setData(null)
      load()
    }
    // eslint-disable-next-line
  }, [open, transaction && transaction.id])

  const events = (data && data.events) || []

  return (
    <Modal
      title={`Payment Timeline${
        transaction && transaction.reference ? ` — ${transaction.reference}` : ''
      }`}
      open={open}
      onCancel={onClose}
      footer={[
        <Button
          key="refresh"
          icon={<ReloadOutlined />}
          onClick={load}
          loading={loading}
        >
          Refresh
        </Button>,
        <Button key="close" type="primary" onClick={onClose}>
          Close
        </Button>
      ]}
      width={640}
      destroyOnClose
    >
      {loading && !data ? (
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <Spin />
        </div>
      ) : !data ? (
        <Empty description="No timeline" />
      ) : (
        <>
          {data.note ? (
            <Alert
              type={
                data.status === 'failed'
                  ? 'error'
                  : data.status === 'no_attempt'
                  ? 'warning'
                  : 'info'
              }
              showIcon
              style={{ marginBottom: 12 }}
              message={data.note}
            />
          ) : null}

          <Descriptions size="small" column={2} bordered style={{ marginBottom: 16 }}>
            <Descriptions.Item label="Status">
              <Tag color={STATUS_COLOR[data.status] || 'default'}>{data.status}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Gateway">{data.gateway}</Descriptions.Item>
            <Descriptions.Item label="Amount">
              {data.currency} {data.amount || '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Channel">
              {data.channel || '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Attempts">{data.attempts}</Descriptions.Item>
            <Descriptions.Item label="Errors">{data.errors}</Descriptions.Item>
            <Descriptions.Item label="Time spent">
              {data.timeSpentSeconds != null ? `${data.timeSpentSeconds}s` : '—'}
            </Descriptions.Item>
            <Descriptions.Item label="On mobile">
              {data.mobile ? 'Yes' : 'No'}
            </Descriptions.Item>
            {data.gatewayResponse ? (
              <Descriptions.Item label="Reason" span={2}>
                {data.gatewayResponse}
              </Descriptions.Item>
            ) : null}
          </Descriptions>

          {events.length ? (
            <Timeline>
              {events.map((e, i) => (
                <Timeline.Item key={i} color={EVENT_COLOR[e.type] || 'blue'}>
                  <span style={{ textTransform: 'capitalize' }}>{e.type}</span>
                  {e.atSeconds != null ? (
                    <span style={{ color: '#888' }}> · +{e.atSeconds}s</span>
                  ) : null}
                  <div>{e.message || <i>(no message)</i>}</div>
                </Timeline.Item>
              ))}
            </Timeline>
          ) : (
            <Empty description="No attempt events recorded" />
          )}

          <div style={{ marginTop: 8, fontSize: 12, color: '#aaa' }}>
            {data.source === 'cache' ? 'Cached' : 'Live'} ·{' '}
            {data.fetchedAt ? new Date(data.fetchedAt).toLocaleString() : ''}
          </div>
        </>
      )}
    </Modal>
  )
}

PaymentTimelineModal.propTypes = {
  transaction: PropTypes.object,
  open: PropTypes.bool,
  onClose: PropTypes.func
}

export default PaymentTimelineModal
